import { useState, useEffect } from 'react';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { Input } from 'src/components/ui/input';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { RebateAgreementListItem } from 'src/api/rebates/types';

const DELETE_CONFIRMATION_TEXT = 'delete';

interface BulkDeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  agreements: RebateAgreementListItem[];
  loading: boolean;
}

export function BulkDeleteConfirmDialog({
  open,
  onClose,
  onConfirm,
  agreements,
  loading,
}: BulkDeleteConfirmDialogProps) {
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

  // Reset confirmation text when dialog opens
  useEffect(() => {
    if (open) {
      setDeleteConfirmationText('');
    }
  }, [open]);

  const isConfirmDisabled = deleteConfirmationText !== DELETE_CONFIRMATION_TEXT;
  const count = agreements.length;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={`Delete ${count} Rebate Agreement${count !== 1 ? 's' : ''}`}
      confirmText={`Delete ${count} Agreement${count !== 1 ? 's' : ''}`}
      loading={loading}
      dangerous={!isConfirmDisabled}
      disabled={isConfirmDisabled}
      wide
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete the following {count} agreement
          {count !== 1 ? 's' : ''}? This will permanently delete all associated
          data including rebate rules, product groups, supplier payments,
          invoices, and documents.
        </p>

        {/* List of agreements to be deleted */}
        <ScrollArea className="h-40 rounded-md border p-3">
          <ul className="space-y-1">
            {agreements.map((agreement) => (
              <li key={agreement.id} className="text-sm">
                <span className="font-medium">{agreement.name}</span>
                <span className="text-muted-foreground">
                  {' '}
                  ({agreement.supplier.name})
                </span>
              </li>
            ))}
          </ul>
        </ScrollArea>

        <div className="space-y-2">
          <label className="text-sm font-normal text-gray-700">
            This action cannot be undone. Type <b>{DELETE_CONFIRMATION_TEXT}</b>{' '}
            to confirm.
          </label>
          <Input
            type="text"
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e.target.value)}
            placeholder={`Type '${DELETE_CONFIRMATION_TEXT}' to confirm`}
            className="w-full"
            autoFocus
          />
        </div>
      </div>
    </ConfirmDialog>
  );
}
